import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FiHeart, 
  FiShare2, 
  FiChevronLeft, 
  FiChevronRight, 
  FiPhone, 
  FiMail 
} from "react-icons/fi";
import { FaHeart, FaCheckCircle, FaSchool, FaHospital, FaShoppingCart, FaTrain, FaShoppingBag, FaRoad } from "react-icons/fa";
import { MdDirectionsSubway } from "react-icons/md";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { BsCameraFill } from "react-icons/bs";
import { toast } from "react-toastify";
import { isPropertyShortlisted, toggleShortlistProperty } from "../../utils/shortlistUtils";

/**
 * Extracts all available image URLs for a property.
 */
export const getAllPropertyImages = (property) => {
  if (!property) return ["/assets/images/noimage1.png"];
  const list = [];
  if (Array.isArray(property.DeskImg)) {
    property.DeskImg.forEach((img) => {
      if (img && typeof img === "string" && img.trim()) list.push(img.trim());
    });
  }
  if (Array.isArray(property.photos)) {
    property.photos.forEach((img) => {
      if (img && typeof img === "string" && img.trim() && !list.includes(img.trim())) list.push(img.trim());
    });
  }
  if (Array.isArray(property.images)) {
    property.images.forEach((img) => {
      if (img && typeof img === "string" && img.trim() && !list.includes(img.trim())) list.push(img.trim());
    });
  }
  if (Array.isArray(property.mobileimage)) {
    property.mobileimage.forEach((img) => {
      if (img && typeof img === "string" && img.trim() && !list.includes(img.trim())) list.push(img.trim());
    });
  }
  if (property.thumbnail && typeof property.thumbnail === "string" && property.thumbnail.trim()) {
    if (!list.includes(property.thumbnail.trim())) list.push(property.thumbnail.trim());
  }
  return list.length > 0 ? list : ["/assets/images/noimage1.png"];
};

const PropertySearchResultCard = ({
  property,
  index,
  onCardClick,
  onViewPhone,
  onContact,
  isOwner,
  isAuth,
}) => {
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [shortlisted, setShortlisted] = useState(() =>
    isPropertyShortlisted(property)
  );

  useEffect(() => {
    const sync = () => {
      setShortlisted(isPropertyShortlisted(property));
    };
    sync();
    window.addEventListener("mgh_shortlist_updated", sync);
    return () => window.removeEventListener("mgh_shortlist_updated", sync);
  }, [property]);

  const images = getAllPropertyImages(property);

  const handlePrevImg = (e) => {
    e.stopPropagation();
    setCurrentImgIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextImg = (e) => {
    e.stopPropagation();
    setCurrentImgIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleShortlistToggle = (e) => {
    e.stopPropagation();
    const added = toggleShortlistProperty(property);
    setShortlisted(added);
    if (added) {
      toast.success("Property saved to shortlist!", { autoClose: 1500 });
    } else {
      toast.info("Removed from shortlist", { autoClose: 1500 });
    }
  };

  const handleShare = (e) => {
    e.stopPropagation();
    const propertyId = property.propertyID || property._id;
    const apartName =
      property.apartment && typeof property.apartment === "string" && property.apartment.trim() !== ""
        ? property.apartment.trim()
        : "property";
    const shareUrl = `${window.location.origin}/property-info/${encodeURIComponent(apartName)}/${propertyId}`;

    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        toast.success("Property link copied to clipboard!", { autoClose: 1500 });
      }).catch(() => {
        toast.info("Link: " + shareUrl, { autoClose: 2000 });
      });
    } else {
      toast.info("Link: " + shareUrl, { autoClose: 2000 });
    }
  };

  // Title calculation
  const title =
    property.apartment ||
    (property.subCategory ? `${property.subCategory}` : "") ||
    (property.propertyID ? `Property #${property.propertyID}` : "Modern Property");

  // Subtitle calculation
  const subCategoryText = property.subCategory || property.category || "Property";
  const localityText = property.locality || property.city || "Hyderabad";
  const cityText = property.city ? `, ${property.city}` : "";
  const subtitle = `${subCategoryText} in ${localityText}${cityText}`;

  // Area & Specs calculation
  const areaValue =
    property.carpetArea ||
    property.superbuildupArea ||
    property.plotArea ||
    property.area ||
    "3600";
  const areaUnit =
    property.carpetAreaMesurmentType ||
    property.superbuildupAreaMesurmentType ||
    "sq.ft.";
  const areaLabel = property.carpetArea
    ? "Carpet Area"
    : property.superbuildupArea
    ? "Super Built-up Area"
    : property.plotArea
    ? "Plot Area"
    : "Carpet Area";

  // Price formatting
  const priceDisplay = property.visiblePrice
    ? String(property.visiblePrice).startsWith("₹")
      ? property.visiblePrice
      : `₹ ${property.visiblePrice}`
    : "₹ Price on Request";

  const pricePerSqft = property.pricePerSqft
    ? `₹ ${property.pricePerSqft} per sq.ft.`
    : "";

  // Proximity flags
  const hasMetro = property.closetoMetroStation === true;
  const hasSchool = property.closetoSchool === true;
  const hasHospital = property.closetoHospital === true;
  const hasMarket = property.closetoMarket === true;
  const hasRailway = property.closetoRailwayStation === true;
  const hasMall = property.closetoMall === true;
  const hasHighway = property.closetoHighway === true;

  const hasAnyProximity =
    hasMetro || hasSchool || hasHospital || hasMarket || hasRailway || hasMall || hasHighway;

  // Description snippet
  const rawDescription =
    property.unicProperty ||
    `This ${subCategoryText} is available for ${
      property.category === "Sell" ? "Sale" : property.category || "Sale"
    } in ${localityText}, Hyderabad. Prime location with excellent connectivity.`;

  const needsTruncate = rawDescription.length > 95;
  const shortDescription = needsTruncate
    ? `${rawDescription.slice(0, 95)}...`
    : rawDescription;

  // Status badge
  const statusBadge =
    property.availabilityStatus ||
    (property.category === "Rent"
      ? "READY TO MOVE"
      : property.construcStatus || "UNDER CONSTRUCTION");

  // Owner Name
  const ownerName =
    property.postUserName ||
    property.postUserPosition ||
    "Sree vigneshwara";

  return (
    <article
      className="mgh-result-card"
      key={index}
      onClick={(e) => onCardClick(e, property)}
    >
      {/* Left: Image Section */}
      <div className="mgh-card-img-wrap">
        {/* Verified Badge */}
        <div className="mgh-card-badge-verified">
          <FaCheckCircle className="mgh-verified-icon" />
          <span>Verified</span>
        </div>

        {/* Property Main Image */}
        <img
          src={images[currentImgIndex]}
          alt={title}
          className="mgh-card-main-img"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/assets/images/noimage1.png";
          }}
        />

        {/* Carousel Arrows (if multiple photos) */}
        {images.length > 1 && (
          <>
            <button
              type="button"
              className="mgh-card-arrow mgh-card-arrow-left"
              onClick={handlePrevImg}
              aria-label="Previous image"
            >
              <FiChevronLeft />
            </button>
            <button
              type="button"
              className="mgh-card-arrow mgh-card-arrow-right"
              onClick={handleNextImg}
              aria-label="Next image"
            >
              <FiChevronRight />
            </button>
          </>
        )}

        {/* Photo Counter Pill */}
        <div className="mgh-card-img-counter">
          <BsCameraFill />
          <span>
            {currentImgIndex + 1} / {images.length}
          </span>
        </div>
      </div>

      {/* Right: Details Section */}
      <div className="mgh-card-content">
        {/* Top Row: Title, Subtitle & Action Icons */}
        <div className="mgh-card-header-row">
          <div className="mgh-card-title-group">
            <h3 className="mgh-card-title">{title}</h3>
            <div className="mgh-card-subtitle">
              <HiOutlineLocationMarker className="mgh-loc-pin" />
              <span>{subtitle}</span>
            </div>
          </div>

          <div className="mgh-card-actions-group" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={`mgh-action-icon-btn ${shortlisted ? "mgh-shortlisted" : ""}`}
              onClick={handleShortlistToggle}
              title={shortlisted ? "Remove from shortlist" : "Save to shortlist"}
            >
              {shortlisted ? <FaHeart color="#ef4444" /> : <FiHeart />}
            </button>
            <button
              type="button"
              className="mgh-action-icon-btn"
              onClick={handleShare}
              title="Share property"
            >
              <FiShare2 />
            </button>
          </div>
        </div>

        {/* Price & Specs Row */}
        <div className="mgh-card-price-specs-row">
          <div className="mgh-price-col">
            <div className="mgh-card-price">{priceDisplay}</div>
            {pricePerSqft && (
              <div className="mgh-card-price-sub">{pricePerSqft}</div>
            )}
          </div>

          <div className="mgh-specs-col">
            <div className="mgh-card-area">
              {areaValue} <span className="mgh-area-unit">{areaUnit}</span>
            </div>
            <div className="mgh-card-area-sub">{areaLabel}</div>
          </div>
        </div>

        {/* Proximity / Amenities Pills Row */}
        <div className="mgh-card-proximity-row">
          {hasMetro && (
            <span className="mgh-prox-pill mgh-prox-metro">
              <MdDirectionsSubway /> Close To Metro Station
            </span>
          )}
          {hasSchool && (
            <span className="mgh-prox-pill mgh-prox-school">
              <FaSchool /> Close To School
            </span>
          )}
          {hasHospital && (
            <span className="mgh-prox-pill mgh-prox-hospital">
              <FaHospital /> Close To Hospital
            </span>
          )}
          {hasMarket && (
            <span className="mgh-prox-pill mgh-prox-market">
              <FaShoppingCart /> Close To Market
            </span>
          )}
          {hasRailway && (
            <span className="mgh-prox-pill mgh-prox-railway">
              <FaTrain /> Close To Railway Station
            </span>
          )}
          {hasMall && (
            <span className="mgh-prox-pill mgh-prox-mall">
              <FaShoppingBag /> Close To Mall
            </span>
          )}
          {hasHighway && (
            <span className="mgh-prox-pill mgh-prox-highway">
              <FaRoad /> Close To Highway
            </span>
          )}

          {/* Fallback default amenity pills to match reference styling if none flagged */}
          {!hasAnyProximity && (
            <>
              <span className="mgh-prox-pill mgh-prox-school">
                <FaSchool /> Close To School
              </span>
              <span className="mgh-prox-pill mgh-prox-hospital">
                <FaHospital /> Close To Hospital
              </span>
              <span className="mgh-prox-pill mgh-prox-market">
                <FaShoppingCart /> Close To Market
              </span>
            </>
          )}
        </div>

        {/* Description Snippet with More/Less */}
        <div className="mgh-card-description" onClick={(e) => e.stopPropagation()}>
          <p>
            {isExpanded ? rawDescription : shortDescription}{" "}
            {needsTruncate && (
              <button
                type="button"
                className="mgh-more-btn"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? "less" : "more"}
              </button>
            )}
          </p>
        </div>

        {/* Status Badge */}
        <div className="mgh-card-status-wrap">
          <span className="mgh-status-badge">{statusBadge}</span>
        </div>

        {/* Bottom Row: Owner Name + CTA Buttons */}
        <div className="mgh-card-footer-row" onClick={(e) => e.stopPropagation()}>
          <div className="mgh-card-owner">
            <span className="mgh-owner-label">Owner: </span>
            <strong className="mgh-owner-val">{ownerName}</strong>
          </div>

          <div className="mgh-card-btn-group">
            <button
              type="button"
              className="mgh-btn-outline-orange"
              onClick={(e) => onViewPhone(e, property)}
            >
              <FiPhone className="mgh-btn-icon" />
              <span>View Phone Number</span>
            </button>

            <button
              type="button"
              className="mgh-btn-solid-orange"
              onClick={(e) => onContact(e, property)}
            >
              <FiMail className="mgh-btn-icon" />
              <span>Contact {property.postUserPosition || "Owner"}</span>
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default PropertySearchResultCard;
